import { useTranslation } from 'react-i18next';
import { ChangeEvent, useRef, useState } from 'react';
import { HiddenInput } from './index.styles';
import { useDropbox } from '../../dropbox/index';
import { composeMediaInteractions } from '../../composeMediaInteractions';
import { useComposeBoxContext } from '../../composeBoxContext';
import { ComposeToolbarDropdown, OptionItem } from '../../../ComposeToolbarDropdown';
import { COMPOSE_TABS } from '../../consts';

export const MediaAttacherDropdown = () => {
  const [isUploading, setIsUploading] = useState(false);
  const { openDropbox } = useDropbox();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const {
    uploadFromDevice,
    isMediaAttacherOpen,
    activeComposeBoxTab,
    setIsCanvaModalOpen,
    setIsFlickrModalOpen,
    setIsGiphyModalOpen,
    setIsPexelsModalOpen,
    setIsRecentlyUsedModalOpen,
    setIsURLModalOpen,
    setIsYoutubeModalOpen,
    setIsGoogleDriveModalOpen,
  } = useComposeBoxContext();
  const { t } = useTranslation('common_terms');

  const device = 'mad-upload-from-device';
  const recentlyUsed = 'mad-upload-recently-used';
  const fromUrl = 'mad-upload-from-url';
  const canva = 'mad-upload-canva';
  const youtubeLink = 'mad-upload-youtube';
  const flickr = 'mad-upload-flickr';
  const pexels = 'mad-upload-pexels';
  const giphy = 'mad-upload-giphy';
  const dropbox = 'mad-upload-dropbox';
  const googleDrive = 'mad-upload-google-drive';

  // Dropping files isn't supported on the bulk importer's tab (a dropped file has no row to
  // land on - see DragAndDrop), so don't advertise it there.
  const isBulkImport = activeComposeBoxTab === COMPOSE_TABS.BULK_IMPORT;

  const portal = window.document.getElementById(`attachment-button-compose-${activeComposeBoxTab}`);

  const onDeviceUpload = (event: ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;

    setIsUploading(false);
    composeMediaInteractions.setIsMediaAttacherOpen(false);

    if (!files) return;
    uploadFromDevice(Array.from(files));
  };

  const handleClick = (option: string) => {
    composeMediaInteractions.setIsMediaAttacherOpen(false);

    if (option === device) return fileInputRef.current?.click();
    if (option === recentlyUsed) return setIsRecentlyUsedModalOpen(true);
    if (option === fromUrl) return setIsURLModalOpen(true);
    if (option === canva) return setIsCanvaModalOpen(true);
    if (option === youtubeLink) return setIsYoutubeModalOpen(true);
    if (option === googleDrive) return setIsGoogleDriveModalOpen(true);
    if (option === flickr) return setIsFlickrModalOpen(true);
    if (option === pexels) return setIsPexelsModalOpen(true);
    if (option === giphy) return setIsGiphyModalOpen(true);
    if (option === dropbox) return openDropbox();
  };

  const fullOptionList: OptionItem[] = [
    {
      icon: 'upload',
      id: device,
      label: t('upload_from_device'),
      extraLabel: isBulkImport ? '' : t('or_drop_files') || '',
      testId: 'attach-option-device',
    },
    {
      icon: 'clock',
      id: recentlyUsed,
      label: t('recently_used'),
      testId: 'attach-option-recent',
    },
    {
      icon: 'globe',
      id: fromUrl,
      label: t('from_url'),
      testId: 'attach-option-url',
    },
    {
      icon: 'canva',
      id: canva,
      label: t('canva'),
      testId: 'attach-option-canva',
    },
    {
      icon: 'youtube_color',
      id: youtubeLink,
      label: t('youtube_link'),
      testId: 'attach-option-youtube',
    },
    {
      icon: 'flickr',
      id: flickr,
      label: t('flickr'),
      testId: 'attach-option-flickr',
    },
    {
      icon: 'pexels',
      id: pexels,
      label: t('pexels'),
      testId: 'attach-option-pexels',
    },
    {
      icon: 'giphy',
      id: giphy,
      label: t('giphy'),
      testId: 'attach-option-giphy',
    },
    {
      icon: 'dropbox',
      id: dropbox,
      label: t('dropbox'),
      testId: 'attach-option-dropbox',
    },
    {
      icon: 'google_drive',
      id: googleDrive,
      label: t('google_drive'),
      testId: 'attach-option-gdrive',
    },
  ];

  // The bulk importer's own (legacy) attachment modal only ever supported: uploading a
  // file, attaching from a URL, picking from the media library ("recently used"),
  // searching stock media (Flickr/Pexels/Giphy), or picking from cloud storage
  // (Dropbox/Google Drive). Canva and "YouTube link" are newer composer-only
  // capabilities that were never part of that legacy modal, so they're excluded here -
  // opening this dropdown from the bulk importer should keep supporting exactly what
  // it already supported, not gain new capabilities.
  const bulkImportExcludedOptionIds = new Set([canva, youtubeLink]);
  const optionList = isBulkImport ? fullOptionList.filter((option) => !bulkImportExcludedOptionIds.has(option.id)) : fullOptionList;

  // The bulk importer attaches exactly one media item per row (no multi-image carousel per
  // row) - restrict the OS file picker to a single selection when opened from its pseudo tab,
  // or a multi-select here would race several uploads into the same row with no defined
  // "winner" (useUploadFiles has no concurrency limiting of its own either).
  const allowMultipleFiles = !isBulkImport;

  return (
    <>
      <HiddenInput
        id="fileToUpload"
        accept="
          image/jpeg,
          image/png,
          image/gif,
          image/webp,
          image/bmp,
          video/mp4,
          .mov,
          application/pdf"
        multiple={allowMultipleFiles}
        name="files[]"
        onChange={onDeviceUpload}
        onClick={(event) => {
          setIsUploading(true);
          event.currentTarget.value = '';
        }}
        ref={fileInputRef}
        type="file"
      />
      <ComposeToolbarDropdown
        buttonId="compose-attachment"
        isVisible={isMediaAttacherOpen}
        onClose={() => !isUploading && composeMediaInteractions.setIsMediaAttacherOpen(false)}
        onSelect={(option) => handleClick(option)}
        options={optionList}
        portal={portal}
      />
    </>
  );
};
